import { Component, type ErrorInfo, type ReactNode } from 'react';

export type ScreenErrorFallbackProps = {
  error: Error;
  /** The Sentry event id when reporting is wired; shown to the user as a support reference. */
  referenceId: string | undefined;
  reset: () => void;
};

export type ScreenErrorBoundaryProps = {
  children: ReactNode;
  fallback: (props: ScreenErrorFallbackProps) => ReactNode;
  /** Report the error and return a reference id. Sentry's captureException returns one. */
  report?: (error: Error, info: ErrorInfo) => string | undefined;
};

type State = { error: Error | null; referenceId: string | undefined };

const toError = (thrown: unknown): Error => (thrown instanceof Error ? thrown : new Error(String(thrown)));

/**
 * Wraps each screen. Modules throw; the screen boundary reports and renders the fallback. Modules never have
 * their own boundaries (see CLAUDE.md).
 */
export class ScreenErrorBoundary extends Component<ScreenErrorBoundaryProps, State> {
  override state: State = { error: null, referenceId: undefined };

  static getDerivedStateFromError(thrown: unknown): Partial<State> {
    return { error: toError(thrown) };
  }

  override componentDidCatch(thrown: unknown, info: ErrorInfo): void {
    const referenceId = this.props.report?.(toError(thrown), info);
    if (referenceId !== undefined) {
      this.setState({ referenceId });
    }
  }

  private readonly reset = (): void => {
    this.setState({ error: null, referenceId: undefined });
  };

  override render(): ReactNode {
    if (this.state.error !== null) {
      return this.props.fallback({ error: this.state.error, referenceId: this.state.referenceId, reset: this.reset });
    }
    return this.props.children;
  }
}
